import styles from '@github-ui/commenting/AvatarStyles.module.css'
import {IssueComment} from '@github-ui/commenting/IssueComment'
import {isFeatureEnabled} from '@github-ui/feature-flags'
import {GitHubAvatar} from '@github-ui/github-avatar'
import {useGetHovercardAttributesForType} from '@github-ui/hovercards/use-get-hovercard-attributes-for-type'
import {
  AddedToProjectV2Event,
  type AddedToProjectV2EventFragment,
} from '@github-ui/timeline-items/AddedToProjectV2Event'
import {AssignedEvent, type AssignedEventFragment} from '@github-ui/timeline-items/AssignedEvent'
import {BlockedByAddedEvent, type BlockedByAddedEventFragment} from '@github-ui/timeline-items/BlockedByAddedEvent'
import {
  BlockedByRemovedEvent,
  type BlockedByRemovedEventFragment,
} from '@github-ui/timeline-items/BlockedByRemovedEvent'
import {BlockingAddedEvent, type BlockingAddedEventFragment} from '@github-ui/timeline-items/BlockingAddedEvent'
import {BlockingRemovedEvent, type BlockingRemovedEventFragment} from '@github-ui/timeline-items/BlockingRemovedEvent'
import {ClosedEvent, type ClosedEventFragment} from '@github-ui/timeline-items/ClosedEvent'
import {CommentDeletedEvent, type CommentDeletedEventFragment} from '@github-ui/timeline-items/CommentDeletedEvent'
import {ConnectedEvent, type ConnectedEventFragment} from '@github-ui/timeline-items/ConnectedEvent'
import {
  ConvertedFromDraftEvent,
  type ConvertedFromDraftEventFragment,
} from '@github-ui/timeline-items/ConvertedFromDraftEvent'
import {
  ConvertedToDiscussionEvent,
  type ConvertedToDiscussionEventFragment,
} from '@github-ui/timeline-items/ConvertedToDiscussionEvent'
import {CrossReferencedEvent, type ReferenceTypes} from '@github-ui/timeline-items/CrossReferencedEvent'
import {DemilestonedEvent} from '@github-ui/timeline-items/DemilestonedEvent'
import {DisconnectedEvent, type DisconnectedEventFragment} from '@github-ui/timeline-items/DisconnectedEvent'
import {IssueCommentPinnedEvent} from '@github-ui/timeline-items/IssueCommentPinnedEvent'
import {IssueCommentUnpinnedEvent} from '@github-ui/timeline-items/IssueCommentUnpinnedEvent'
import {IssueFieldAddedEvent} from '@github-ui/timeline-items/IssueFieldAddedEvent'
import {IssueFieldChangedEvent} from '@github-ui/timeline-items/IssueFieldChangedEvent'
import {IssueFieldRemovedEvent} from '@github-ui/timeline-items/IssueFieldRemovedEvent'
import {IssueTypeAddedEvent, type IssueTypeAddedEventFragment} from '@github-ui/timeline-items/IssueTypeAddedEvent'
import {
  IssueTypeChangedEvent,
  type IssueTypeChangedEventFragment,
} from '@github-ui/timeline-items/IssueTypeChangedEvent'
import {
  IssueTypeRemovedEvent,
  type IssueTypeRemovedEventFragment,
} from '@github-ui/timeline-items/IssueTypeRemovedEvent'
import {LabeledEvent, type LabeledEventFragment} from '@github-ui/timeline-items/LabeledEvent'
import {wrapElement} from '@github-ui/timeline-items/LayoutHelpers'
import {LockedEvent, type LockedEventFragment} from '@github-ui/timeline-items/LockedEvent'
import {MarkedAsDuplicateEvent} from '@github-ui/timeline-items/MarkedAsDuplicateEvent'
import {MentionedEvent, type MentionedEventFragment} from '@github-ui/timeline-items/MentionedEvent'
import {MilestonedEvent} from '@github-ui/timeline-items/MilestonedEvent'
import {
  ParentIssueAddedEvent,
  type ParentIssueAddedEventFragment,
} from '@github-ui/timeline-items/ParentIssueAddedEvent'
import {
  ParentIssueRemovedEvent,
  type ParentIssueRemovedEventFragment,
} from '@github-ui/timeline-items/ParentIssueRemovedEvent'
import {PinnedEvent, type PinnedEventFragment} from '@github-ui/timeline-items/PinnedEvent'
import {
  ProjectV2ItemStatusChangedEvent,
  type ProjectV2ItemStatusChangedEventFragment,
} from '@github-ui/timeline-items/ProjectV2ItemStatusChangedEvent'
import {ReferencedEvent} from '@github-ui/timeline-items/ReferencedEvent'
import {
  RemovedFromProjectV2Event,
  type RemovedFromProjectV2EventFragment,
} from '@github-ui/timeline-items/RemovedFromProjectV2Event'
import {RenamedTitleEvent, type RenamedTitleEventFragment} from '@github-ui/timeline-items/RenamedTitleEvent'
import {ReopenedEvent, type ReopenedEventFragment} from '@github-ui/timeline-items/ReopenedEvent'
import type {PreloadedSecondaryQueryType} from '@github-ui/timeline-items/SecondaryTimelineQuery'
import {SubIssueAddedEvent, type SubIssueAddedEventFragment} from '@github-ui/timeline-items/SubIssueAddedEvent'
import {SubIssueRemovedEvent, type SubIssueRemovedEventFragment} from '@github-ui/timeline-items/SubIssueRemovedEvent'
import {SubscribedEvent, type SubscribedEventFragment} from '@github-ui/timeline-items/SubscribedEvent'
import {TimelineRowBorder, type TimelineRowBorderCommentParams} from '@github-ui/timeline-items/TimelineRowBorder'
import {TransferredEvent, type TransferredEventFragment} from '@github-ui/timeline-items/TransferredEvent'
import {UnassignedEvent, type UnassignedEventFragment} from '@github-ui/timeline-items/UnassignedEvent'
import {UnlabeledEvent, type UnlabeledEventFragment} from '@github-ui/timeline-items/UnlabeledEvent'
import {UnlockedEvent, type UnlockedEventFragment} from '@github-ui/timeline-items/UnlockedEvent'
import {UnmarkedAsDuplicateEvent} from '@github-ui/timeline-items/UnmarkedAsDuplicateEvent'
import {UnpinnedEvent, type UnpinnedEventFragment} from '@github-ui/timeline-items/UnpinnedEvent'
import {UnsubscribedEvent, type UnsubscribedEventFragment} from '@github-ui/timeline-items/UnsubscribedEvent'
import {UserBlockedEvent, type UserBlockedEventFragment} from '@github-ui/timeline-items/UserBlockedEvent'
import {VALUES} from '@github-ui/timeline-items/Values'
import {Link} from '@primer/react'
import type {ReactElement, RefObject} from 'react'
import {graphql} from 'relay-runtime'

import type {RolledUpTimelineItem} from '../../utils/timeline-rollups'
import type {OptionConfig} from '../OptionConfig'
import type {IssueTimelineItem$data} from './__generated__/IssueTimelineItem.graphql'
import {useTimelineConnectionIds} from './TimelineConnectionContext'

/**
 * Required props to render a timeline item event component.
 */
type EventItemInnerProps = {
  queryRef: IssueTimelineItem$data
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  rollupGroup?: Record<string, any[]>
  currentIssueId: string
  repositoryId: string
  repositoryNameWithOwner: string
  ownerLogin: string
  issueUrl: string
  timelineEventBaseUrl: string
  highlightedEventId?: string
  refAttribute?: React.RefObject<HTMLLIElement | null>
  referenceTypes?: ReferenceTypes
  viewer: string | null
  onLinkClick?: (event: MouseEvent) => void
  secondaryTimelineQueryRef?: PreloadedSecondaryQueryType
}

function coerceTimelineQueryRef<TFragment>(queryRef: IssueTimelineItem$data): TFragment {
  return queryRef as TFragment
}

/**
 * Record of all possible timeline items that can be rendered (IssueComment is not included).
 */
const TIMELINE_ITEMS: Record<string, ((args: EventItemInnerProps) => React.ReactElement | null) | undefined> = {
  // RELAY-CLEANUP(IssueTimelineItem): remove cast when IssueTimelineItem leaves Relay.
  SubscribedEvent: ({queryRef, ...props}) => (
    <SubscribedEvent {...props} queryRef={coerceTimelineQueryRef<SubscribedEventFragment>(queryRef)} />
  ),
  // RELAY-CLEANUP(IssueTimelineItem): remove cast when IssueTimelineItem leaves Relay.
  UnsubscribedEvent: ({queryRef, ...props}) => (
    <UnsubscribedEvent {...props} queryRef={coerceTimelineQueryRef<UnsubscribedEventFragment>(queryRef)} />
  ),
  // RELAY-CLEANUP(IssueTimelineItem): remove cast when IssueTimelineItem leaves Relay.
  MentionedEvent: ({queryRef, ...props}) => (
    <MentionedEvent {...props} queryRef={coerceTimelineQueryRef<MentionedEventFragment>(queryRef)} />
  ),
  // RELAY-CLEANUP(IssueTimelineItem): open-enum `stateReason` while IssueTimelineItem remains Relay-based.
  // Remove when IssueTimelineItem leaves Relay.
  ClosedEvent: ({queryRef, ...props}) => (
    <ClosedEvent {...props} queryRef={coerceTimelineQueryRef<ClosedEventFragment>(queryRef)} />
  ),
  // RELAY-CLEANUP(IssueTimelineItem): remove cast when IssueTimelineItem leaves Relay.
  ReopenedEvent: ({queryRef, ...props}) => (
    <ReopenedEvent {...props} queryRef={coerceTimelineQueryRef<ReopenedEventFragment>(queryRef)} />
  ),
  // RELAY-CLEANUP(IssueTimelineItem): remove cast when IssueTimelineItem leaves Relay.
  RenamedTitleEvent: ({queryRef, ...props}) => (
    <RenamedTitleEvent {...props} queryRef={coerceTimelineQueryRef<RenamedTitleEventFragment>(queryRef)} />
  ),
  // RELAY-CLEANUP(IssueTimelineItem): remove cast when IssueTimelineItem leaves Relay.
  LockedEvent: ({queryRef, ...props}) => (
    <LockedEvent {...props} queryRef={coerceTimelineQueryRef<LockedEventFragment>(queryRef)} />
  ),
  // RELAY-CLEANUP(IssueTimelineItem): remove cast when IssueTimelineItem leaves Relay.
  UnlockedEvent: ({queryRef, ...props}) => (
    <UnlockedEvent {...props} queryRef={coerceTimelineQueryRef<UnlockedEventFragment>(queryRef)} />
  ),
  // RELAY-CLEANUP(IssueTimelineItem): remove cast when IssueTimelineItem leaves Relay.
  PinnedEvent: ({queryRef, ...props}) => (
    <PinnedEvent {...props} queryRef={coerceTimelineQueryRef<PinnedEventFragment>(queryRef)} />
  ),
  IssueCommentPinnedEvent,
  // RELAY-CLEANUP(IssueTimelineItem): remove cast when IssueTimelineItem leaves Relay.
  UnpinnedEvent: ({queryRef, ...props}) => (
    <UnpinnedEvent {...props} queryRef={coerceTimelineQueryRef<UnpinnedEventFragment>(queryRef)} />
  ),
  IssueCommentUnpinnedEvent,
  // RELAY-CLEANUP(IssueTimelineItem): remove cast when IssueTimelineItem leaves Relay.
  LabeledEvent: ({queryRef, ...props}) => (
    <LabeledEvent {...props} queryRef={coerceTimelineQueryRef<LabeledEventFragment>(queryRef)} />
  ),
  // RELAY-CLEANUP(IssueTimelineItem): remove cast when IssueTimelineItem leaves Relay.
  UnlabeledEvent: ({queryRef, ...props}) => (
    <UnlabeledEvent {...props} queryRef={coerceTimelineQueryRef<UnlabeledEventFragment>(queryRef)} />
  ),
  // RELAY-CLEANUP(IssueTimelineItem): remove cast when IssueTimelineItem leaves Relay.
  UnassignedEvent: ({queryRef, ...props}) => (
    <UnassignedEvent {...props} queryRef={coerceTimelineQueryRef<UnassignedEventFragment>(queryRef)} />
  ),
  // RELAY-CLEANUP(IssueTimelineItem): open-enum cast while IssueTimelineItem remains Relay-based.
  // Remove when IssueTimelineItem leaves Relay.
  AssignedEvent: ({queryRef, ...props}) => (
    <AssignedEvent {...props} queryRef={coerceTimelineQueryRef<AssignedEventFragment>(queryRef)} />
  ),
  // RELAY-CLEANUP(IssueTimelineItem): remove cast when IssueTimelineItem leaves Relay.
  BlockedByAddedEvent: ({queryRef, ...props}) => (
    <BlockedByAddedEvent {...props} queryRef={coerceTimelineQueryRef<BlockedByAddedEventFragment>(queryRef)} />
  ),
  // RELAY-CLEANUP(IssueTimelineItem): remove cast when IssueTimelineItem leaves Relay.
  BlockedByRemovedEvent: ({queryRef, ...props}) => (
    <BlockedByRemovedEvent {...props} queryRef={coerceTimelineQueryRef<BlockedByRemovedEventFragment>(queryRef)} />
  ),
  // RELAY-CLEANUP(IssueTimelineItem): remove cast when IssueTimelineItem leaves Relay.
  BlockingAddedEvent: ({queryRef, ...props}) => (
    <BlockingAddedEvent {...props} queryRef={coerceTimelineQueryRef<BlockingAddedEventFragment>(queryRef)} />
  ),
  // RELAY-CLEANUP(IssueTimelineItem): remove cast when IssueTimelineItem leaves Relay.
  BlockingRemovedEvent: ({queryRef, ...props}) => (
    <BlockingRemovedEvent {...props} queryRef={coerceTimelineQueryRef<BlockingRemovedEventFragment>(queryRef)} />
  ),
  // RELAY-CLEANUP(IssueTimelineItem): remove cast when IssueTimelineItem leaves Relay.
  CommentDeletedEvent: ({queryRef, ...props}) => (
    <CommentDeletedEvent {...props} queryRef={coerceTimelineQueryRef<CommentDeletedEventFragment>(queryRef)} />
  ),
  // RELAY-CLEANUP(IssueTimelineItem): remove cast when IssueTimelineItem leaves Relay.
  UserBlockedEvent: ({queryRef, ...props}) => (
    <UserBlockedEvent {...props} queryRef={coerceTimelineQueryRef<UserBlockedEventFragment>(queryRef)} />
  ),
  MilestonedEvent,
  DemilestonedEvent,
  CrossReferencedEvent,
  ReferencedEvent,
  // RELAY-CLEANUP(IssueTimelineItem): remove cast when IssueTimelineItem leaves Relay.
  // `subject` is a union, so the Relay parent type and the generated fragment type do not
  // overlap; keep using the shared coercion helper until the parent leaves Relay.
  ConnectedEvent: ({queryRef, ...props}) => (
    <ConnectedEvent {...props} queryRef={coerceTimelineQueryRef<ConnectedEventFragment>(queryRef)} />
  ),
  // RELAY-CLEANUP(IssueTimelineItem): remove cast when IssueTimelineItem leaves Relay.
  TransferredEvent: ({queryRef, ...props}) => (
    <TransferredEvent {...props} queryRef={coerceTimelineQueryRef<TransferredEventFragment>(queryRef)} />
  ),
  // RELAY-CLEANUP(IssueTimelineItem): remove cast when IssueTimelineItem leaves Relay.
  // `subject` is a union, so the Relay parent type and the generated fragment type do not
  // overlap; keep using the shared coercion helper until the parent leaves Relay.
  DisconnectedEvent: ({queryRef, ...props}) => (
    <DisconnectedEvent {...props} queryRef={coerceTimelineQueryRef<DisconnectedEventFragment>(queryRef)} />
  ),
  MarkedAsDuplicateEvent,
  UnmarkedAsDuplicateEvent,
  // RELAY-CLEANUP(IssueTimelineItem): remove cast when IssueTimelineItem leaves Relay.
  ConvertedToDiscussionEvent: ({queryRef, ...props}) => (
    <ConvertedToDiscussionEvent
      {...props}
      queryRef={coerceTimelineQueryRef<ConvertedToDiscussionEventFragment>(queryRef)}
    />
  ),
  // RELAY-CLEANUP(IssueTimelineItem): boundary cast while IssueTimelineItem remains Relay-based.
  // Remove when IssueTimelineItem leaves Relay.
  AddedToProjectV2Event: ({queryRef, ...props}) => (
    <AddedToProjectV2Event {...props} queryRef={coerceTimelineQueryRef<AddedToProjectV2EventFragment>(queryRef)} />
  ),
  // RELAY-CLEANUP(IssueTimelineItem): remove cast when IssueTimelineItem leaves Relay.
  RemovedFromProjectV2Event: ({queryRef, ...props}) => (
    <RemovedFromProjectV2Event
      {...props}
      queryRef={coerceTimelineQueryRef<RemovedFromProjectV2EventFragment>(queryRef)}
    />
  ),
  // RELAY-CLEANUP(IssueTimelineItem): remove cast when IssueTimelineItem leaves Relay.
  ProjectV2ItemStatusChangedEvent: ({queryRef, ...props}) => (
    <ProjectV2ItemStatusChangedEvent
      {...props}
      queryRef={coerceTimelineQueryRef<ProjectV2ItemStatusChangedEventFragment>(queryRef)}
    />
  ),
  // RELAY-CLEANUP(IssueTimelineItem): remove cast when IssueTimelineItem leaves Relay.
  ConvertedFromDraftEvent: ({queryRef, ...props}) => (
    <ConvertedFromDraftEvent {...props} queryRef={coerceTimelineQueryRef<ConvertedFromDraftEventFragment>(queryRef)} />
  ),
  // RELAY-CLEANUP(IssueTimelineItem): remove cast when IssueTimelineItem leaves Relay.
  SubIssueAddedEvent: ({queryRef, ...props}) => (
    <SubIssueAddedEvent {...props} queryRef={coerceTimelineQueryRef<SubIssueAddedEventFragment>(queryRef)} />
  ),
  // RELAY-CLEANUP(IssueTimelineItem): remove cast when IssueTimelineItem leaves Relay.
  SubIssueRemovedEvent: ({queryRef, ...props}) => (
    <SubIssueRemovedEvent {...props} queryRef={coerceTimelineQueryRef<SubIssueRemovedEventFragment>(queryRef)} />
  ),
  // RELAY-CLEANUP(IssueTimelineItem): remove cast when IssueTimelineItem leaves Relay.
  ParentIssueAddedEvent: ({queryRef, ...props}) => (
    <ParentIssueAddedEvent {...props} queryRef={coerceTimelineQueryRef<ParentIssueAddedEventFragment>(queryRef)} />
  ),
  // RELAY-CLEANUP(IssueTimelineItem): remove cast when IssueTimelineItem leaves Relay.
  ParentIssueRemovedEvent: ({queryRef, ...props}) => (
    <ParentIssueRemovedEvent {...props} queryRef={coerceTimelineQueryRef<ParentIssueRemovedEventFragment>(queryRef)} />
  ),
  // RELAY-CLEANUP(IssueTimelineItem): remove cast when IssueTimelineItem leaves Relay.
  IssueTypeAddedEvent: ({queryRef, ...props}) => (
    <IssueTypeAddedEvent {...props} queryRef={coerceTimelineQueryRef<IssueTypeAddedEventFragment>(queryRef)} />
  ),
  // RELAY-CLEANUP(IssueTimelineItem): remove cast when IssueTimelineItem leaves Relay.
  IssueTypeRemovedEvent: ({queryRef, ...props}) => (
    <IssueTypeRemovedEvent {...props} queryRef={coerceTimelineQueryRef<IssueTypeRemovedEventFragment>(queryRef)} />
  ),
  // RELAY-CLEANUP(IssueTimelineItem): remove cast when IssueTimelineItem leaves Relay.
  IssueTypeChangedEvent: ({queryRef, ...props}) => (
    <IssueTypeChangedEvent {...props} queryRef={coerceTimelineQueryRef<IssueTypeChangedEventFragment>(queryRef)} />
  ),
  IssueFieldAddedEvent,
  IssueFieldChangedEvent,
  IssueFieldRemovedEvent,
}

// eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-queries
export const TimelineItemFragment = graphql`
  fragment IssueTimelineItem on IssueTimelineItems @inline {
    __id
    __typename
    ... on TimelineEvent {
      databaseId
      createdAt
      actor {
        id
        avatarUrl(size: 64)
        login
        profileResourcePath
        __typename
        ... on Bot {
          isCopilot
          isAgent
          displayName
        }
        ... on User {
          name
        }
        ... on EnterpriseUserAccount {
          name
        }
        ... on Mannequin {
          name
        }
      }
    }
    ... on IssueComment {
      databaseId # this is required for highlighting support as this is a major event
      viewerDidAuthor # this is required for giving the viewer's comments a different border color
      issue {
        author {
          login
        }
      }
      author {
        login
        avatarUrl
        profileUrl
      }
    }
    ... on CrossReferencedEvent {
      source {
        __typename
      }
      willCloseTarget
    }
    ... on LabeledEvent {
      label {
        id
      }
    }
    ... on UnlabeledEvent {
      label {
        id
      }
    }
    ... on AssignedEvent {
      id
      assignee {
        ... on Actor {
          login
        }
        ... on User {
          id
          __typename
          login
          resourcePath
          name
        }
        ... on Mannequin {
          id
          __typename
          login
          resourcePath
          name
        }
        ... on Organization {
          id
          __typename
          login
          resourcePath
        }
        ... on Bot {
          id
          __typename
          login
          resourcePath
          isCopilot
          displayName
          isAgent
        }
      }
      intent {
        rationale
      }
    }
    ... on UnassignedEvent {
      assignee {
        __typename
        ... on User {
          id
        }
        ... on Bot {
          id
        }
        ... on Mannequin {
          id
        }
        ... on Organization {
          id
        }
      }
    }
    ... on MilestonedEvent {
      milestone {
        id
      }
    }
    ... on DemilestonedEvent {
      milestone {
        id
      }
    }
    # Issue field events need issueField.name for rollup deduplication in timeline-rollups.ts
    # The fragment spreads below provide full data for rendering, but deduplication happens
    # before useFragment is called, so we need the name available at the timeline item level.
    ... on IssueFieldAddedEvent {
      issueField {
        ... on IssueFieldText {
          name
        }
        ... on IssueFieldDate {
          name
        }
        ... on IssueFieldNumber {
          name
        }
        ... on IssueFieldSingleSelect {
          name
        }
        ... on IssueFieldMultiSelect {
          name
        }
      }
    }
    ... on IssueFieldChangedEvent {
      issueField {
        ... on IssueFieldText {
          name
        }
        ... on IssueFieldDate {
          name
        }
        ... on IssueFieldNumber {
          name
        }
        ... on IssueFieldSingleSelect {
          name
        }
        ... on IssueFieldMultiSelect {
          name
        }
      }
    }
    ... on IssueFieldRemovedEvent {
      issueField {
        ... on IssueFieldText {
          name
        }
        ... on IssueFieldDate {
          name
        }
        ... on IssueFieldNumber {
          name
        }
        ... on IssueFieldSingleSelect {
          name
        }
        ... on IssueFieldMultiSelect {
          name
        }
      }
    }
    ... on IssueCommentPinnedEvent {
      issueComment {
        ... on IssueComment {
          id
        }
      }
    }
    ... on IssueCommentUnpinnedEvent {
      issueComment {
        ... on IssueComment {
          id
        }
      }
    }
    ...IssueComment_issueComment @dangerously_unaliased_fixme
    ... on SubscribedEvent {
      id
    }
    ... on UnsubscribedEvent {
      id
    }
    ... on MentionedEvent {
      id
    }
    ... on ClosedEvent {
      databaseId
      createdAt
      stateReason
      duplicateOf {
        __typename
        ... on Issue {
          id
          titleHTML
          url
          number
          stateReason
          repository {
            id
            name
            isPrivate
            owner {
              login
            }
          }
        }
        ... on PullRequest {
          id
          pullTitleHTML: titleHTML
          url
          number
          state
          isDraft
          isInMergeQueue
          repository {
            id
            name
            isPrivate
            owner {
              login
            }
          }
        }
      }
      closingProjectItemStatus
      closer {
        __typename
        ... on ProjectV2 {
          url
          title
        }
        ... on PullRequest {
          url
          number
          repository {
            id
            name
            owner {
              login
            }
          }
        }
        ... on Commit {
          url
          abbreviatedOid
          repository {
            name
            owner {
              login
            }
          }
        }
      }
      id
      intent {
        rationale
      }
    }
    ... on ReopenedEvent {
      id
    }
    ... on LockedEvent {
      lockReason
      id
    }
    ... on UnlockedEvent {
      id
    }
    ... on PinnedEvent {
      id
    }
    ... on UnpinnedEvent {
      id
    }
    ... on LabeledEvent {
      id
      label {
        nameHTML
        name
        color
        id
        description
      }
      intent {
        rationale
      }
    }
    ... on RenamedTitleEvent {
      id
      currentTitle
      previousTitle
    }
    ... on UnlabeledEvent {
      id
      label {
        nameHTML
        name
        color
        id
        description
      }
    }
    ... on UnassignedEvent {
      id
      assignee {
        ... on Actor {
          login
        }
        ... on User {
          __typename
          login
          resourcePath
          name
        }
        ... on Mannequin {
          __typename
          login
          resourcePath
          name
        }
        ... on Organization {
          __typename
          login
          resourcePath
        }
        ... on Bot {
          __typename
          login
          resourcePath
          isCopilot
          displayName
          isAgent
        }
      }
    }
    ... on BlockedByAddedEvent {
      id
      blockingIssue {
        __typename
        id
        titleHTML
        url
        number
        stateReason
        repository {
          id
          name
          isPrivate
          owner {
            login
          }
        }
      }
    }
    ... on BlockedByRemovedEvent {
      databaseId
      id
      createdAt
      blockingIssue {
        __typename
        id
        titleHTML
        url
        number
        stateReason
        repository {
          id
          name
          isPrivate
          owner {
            login
          }
        }
      }
    }
    ... on BlockingAddedEvent {
      id
      blockedIssue {
        __typename
        id
        titleHTML
        url
        number
        stateReason
        repository {
          id
          name
          isPrivate
          owner {
            login
          }
        }
      }
    }
    ... on BlockingRemovedEvent {
      id
      blockedIssue {
        __typename
        id
        titleHTML
        url
        number
        stateReason
        repository {
          id
          name
          isPrivate
          owner {
            login
          }
        }
      }
    }
    ... on CommentDeletedEvent {
      id
      deletedCommentAuthor {
        __typename
        login
        ... on User {
          name
        }
        ... on Mannequin {
          name
        }
        ... on EnterpriseUserAccount {
          name
        }
      }
    }
    ... on UserBlockedEvent {
      blockDuration
      id
      blockedUser: subject {
        login
        name
      }
    }
    ... on SubIssueAddedEvent {
      id
      subIssue {
        __typename
        id
        titleHTML
        url
        number
        stateReason
        repository {
          id
          name
          isPrivate
          owner {
            login
          }
        }
        databaseId
      }
    }
    ... on SubIssueRemovedEvent {
      id
      subIssue {
        __typename
        id
        titleHTML
        url
        number
        stateReason
        repository {
          id
          name
          isPrivate
          owner {
            login
          }
        }
        databaseId
      }
    }
    ... on ParentIssueAddedEvent {
      id
      parent {
        __typename
        id
        titleHTML
        url
        number
        stateReason
        repository {
          id
          name
          isPrivate
          owner {
            login
          }
        }
        databaseId
      }
    }
    ... on ParentIssueRemovedEvent {
      id
      parent {
        __typename
        id
        titleHTML
        url
        number
        stateReason
        repository {
          id
          name
          isPrivate
          owner {
            login
          }
        }
        databaseId
      }
    }
    ...MilestonedEvent @dangerously_unaliased_fixme
    ...DemilestonedEvent @dangerously_unaliased_fixme
    ...CrossReferencedEvent @dangerously_unaliased_fixme
    ...ReferencedEvent @dangerously_unaliased_fixme
    ... on ConnectedEvent {
      id
      subject {
        __typename
        ... on PullRequest {
          title
          url
          number
          state
          isDraft
          isInMergeQueue
          repository {
            name
            owner {
              login
            }
          }
        }
      }
    }
    ... on TransferredEvent {
      id
      fromRepository {
        nameWithOwner
        url
      }
    }
    ... on DisconnectedEvent {
      id
      subject {
        __typename
        ... on PullRequest {
          title
          url
          number
          state
          isDraft
          isInMergeQueue
          repository {
            name
            owner {
              login
            }
          }
        }
      }
    }
    ...MarkedAsDuplicateEvent @dangerously_unaliased_fixme
    ...UnmarkedAsDuplicateEvent @dangerously_unaliased_fixme
    ... on ConvertedToDiscussionEvent {
      id
      discussion {
        url
        number
      }
    }
    ... on AddedToProjectV2Event {
      databaseId
      createdAt
      project {
        title
        url
      }
    }
    ... on RemovedFromProjectV2Event {
      id
      project {
        title
        url
      }
    }
    ... on ProjectV2ItemStatusChangedEvent {
      id
      project {
        title
        url
      }
      previousStatus
      status
    }
    ... on ConvertedFromDraftEvent {
      id
    }
    ... on IssueTypeAddedEvent {
      id
      issueType {
        name
        color
      }
      intent {
        rationale
      }
    }
    ... on IssueTypeRemovedEvent {
      id
      issueType {
        name
        color
      }
    }
    ... on IssueTypeChangedEvent {
      id
      issueType {
        name
        color
      }
      prevIssueType {
        name
        color
      }
      intent {
        rationale
      }
    }
    ...IssueFieldChangedEvent @dangerously_unaliased_fixme
    ...IssueFieldAddedEvent @dangerously_unaliased_fixme
    ...IssueFieldRemovedEvent @dangerously_unaliased_fixme
    ...IssueCommentPinnedEvent @dangerously_unaliased_fixme
    ...IssueCommentUnpinnedEvent @dangerously_unaliased_fixme
  }
`

/**
 * Props exclusively propagated to render IssueComment and the other timeline event components.
 *
 * Not used by IssueTimelineItem itself.
 */
export type EventProps = {
  viewer: string | null
  onCommentChange: (id: string) => void
  onCommentReply: (quotedComment: string) => void
  onCommentEditCancel: (id: string) => void
  optionConfig: OptionConfig
}

export type IssueTimelineItemProps = {
  item: RolledUpTimelineItem<IssueTimelineItem$data>
  issueId: string
  repositoryId: string
  repositoryNameWithOwner: string
  ownerLogin: string
  issueUrl: string
  isHighlighted?: boolean
  addDivider?: boolean
  refAttribute?: RefObject<HTMLLIElement | null>
  secondaryTimelineQueryRef?: PreloadedSecondaryQueryType
  showCopilotMenuItem?: boolean
} & EventProps

/**
 * This component renders a single timeline item which can be a timeline event or a comment.
 *
 * It takes a rollup group item as it is intended to support rollup groups.
 */
export const IssueTimelineItem = ({
  item: {item, rollupGroup},
  issueId,
  repositoryId,
  repositoryNameWithOwner,
  ownerLogin,
  issueUrl,
  isHighlighted,
  viewer,
  onCommentChange,
  onCommentReply,
  onCommentEditCancel,
  addDivider = false,
  optionConfig,
  refAttribute,
  secondaryTimelineQueryRef,
  showCopilotMenuItem = false,
}: IssueTimelineItemProps) => {
  const timelineConnectionIds = useTimelineConnectionIds()

  if (!item) return null

  const isMajorEvent = VALUES.timeline.majorEventTypes.includes(item.__typename)

  let itemToRender: ReactElement | null = null
  let commentParams: TimelineRowBorderCommentParams | undefined = undefined
  if (item.__typename === 'IssueComment') {
    commentParams = {
      first: false,
      last: false,
      viewerDidAuthor: item.viewerDidAuthor,
    }
    itemToRender = (
      <IssueComment
        comment={item}
        commentSubjectAuthorLogin={item.issue?.author?.login ?? ''}
        onChange={() => onCommentChange?.(item.__id)}
        onEditCancel={() => onCommentEditCancel?.(item.__id)}
        onReply={quote => onCommentReply?.(quote)}
        onSave={() => onCommentEditCancel?.(item.__id)}
        highlightedCommentId={isHighlighted ? String(item.databaseId) : undefined}
        navigate={optionConfig.navigate}
        refAttribute={refAttribute as React.RefObject<HTMLDivElement | null> | undefined}
        onLinkClick={optionConfig.onLinkClick}
        showCopilotMenuItem={showCopilotMenuItem}
        relayConnectionIds={[timelineConnectionIds.front, timelineConnectionIds.back]}
      />
    )
  }

  const TimelineReactElement = TIMELINE_ITEMS[item.__typename]
  if (TimelineReactElement) {
    itemToRender = (
      <TimelineReactElement
        key={item.__id}
        queryRef={item}
        rollupGroup={rollupGroup}
        currentIssueId={issueId}
        issueUrl={issueUrl}
        repositoryId={repositoryId}
        repositoryNameWithOwner={repositoryNameWithOwner}
        ownerLogin={ownerLogin}
        highlightedEventId={isHighlighted ? String(item.databaseId) : undefined}
        timelineEventBaseUrl={optionConfig.timelineEventBaseUrl || '/issues'}
        refAttribute={refAttribute}
        onLinkClick={optionConfig.onLinkClick}
        viewer={viewer}
        secondaryTimelineQueryRef={secondaryTimelineQueryRef}
      />
    )
  }

  const wrappedElement = (
    <TimelineRowBorder
      key={item.__id}
      ref={refAttribute as React.RefObject<HTMLDivElement | null> | undefined}
      item={item}
      addDivider={addDivider}
      isMajor={isMajorEvent && VALUES.timeline.borderedMajorEventTypes.includes(item.__typename)}
      isHighlighted={item.__typename === 'IssueComment' && isHighlighted}
      commentParams={commentParams}
    >
      {itemToRender}
    </TimelineRowBorder>
  )

  const leadingElement =
    item.__typename === 'IssueComment' && !isFeatureEnabled('issue_inline_avatars') ? (
      <TimelineItemAvatar item={item} addDivider={addDivider} />
    ) : undefined

  return wrapElement(wrappedElement, leadingElement, item.__id)
}

const TimelineItemAvatar = ({item, addDivider}: {item: IssueTimelineItem$data; addDivider: boolean}) => {
  const {avatarUrl, login, profileUrl} = item.author ?? VALUES.ghost
  const getHovercardAttributesForType = useGetHovercardAttributesForType()
  const hovercardAttrs = getHovercardAttributesForType('user', {login})

  return (
    <Link
      href={profileUrl ?? undefined}
      {...(profileUrl ? hovercardAttrs : {})}
      aria-label={`@${login}'s profile`}
      className={`${profileUrl ? styles.avatarLink : styles.avatarWithoutLink} ${styles.avatarOuter}`}
    >
      <GitHubAvatar
        key={login}
        alt={login}
        src={avatarUrl}
        size={40}
        className={`${styles.issueViewerAvatar} ${addDivider ? styles.avatarWithDivider : styles.avatarWithoutDivider}`}
      />
    </Link>
  )
}
